import React, { useCallback, CSSProperties } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Icon, SemanticICONS } from 'semantic-ui-react';
import {
  OpenAPI, Other
} from 'simplydo/interfaces';
import toast from 'react-hot-toast';
import { useAppSelector } from 'store';
import styled from 'styled-components';
import util from 'utils/utils';
import api from 'api';

export const StyledChip = styled.div<{ inverted?: boolean, vertical?: boolean }>`
  display: flex;
  align-items: center;
  ${({ vertical }) => vertical && 'flex-direction: column;'}
  i {
    margin-left: 3px;
    transform: translateY(-3px);
    cursor: pointer;
  }
  ${({ inverted }) => inverted && `
    border-radius: 50px;
    padding: 3px 8px 3px 8px;
    background: #332c2c38;
  `}
`;
export const StyledAvatar = styled(Link)<{ compact?: boolean, vertical?: boolean, withShadow?: boolean, user: OpenAPI.Schemas['User'] }>`
  position:relative;
  height: ${({ compact }) => (compact ? '20px' : '30px')};
  width: ${({ compact }) => (compact ? '20px' : '30px')};
  line-height: inherit;
  border-radius:50%;
  display: inline-block;
  background-color:white;
  background-size:cover;
  background-position: center center;
  margin-right: ${({ vertical }) => !vertical && '5px'};
  background-image: ${({ user }) => `url(${util.avatarUrl(user)});`};
  cursor:pointer;
  vertical-align:middle;
  box-shadow: ${({ withShadow }) => (withShadow ? '0 0 4px rgba(0,0,0,0.2)' : 'none')};
`;
export const StyledText = styled.div<{ numberOfLines?: number }>`
  flex: 1;
  display: inline-block;
  vertical-align: middle;
  ${({ numberOfLines }) => numberOfLines && `
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    > p, a {
      display: block;
      display: -webkit-box;
      -webkit-line-clamp: ${numberOfLines};
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `}
`;
export const StyledName = styled(Link)<{ inverted?: boolean, compact?: boolean, disableLink?: boolean }>`
  ${({ disableLink }) => !disableLink ? `
    margin-bottom: 0px;
    border-bottom: 1px dotted ${({ theme }) => theme.primaryColour};
  ` : ''}
  ${({ inverted }) => (inverted ? 'color: white !important;' : '')}
  ${({ compact }) => compact && 'font-size: 13px;'}
`;

const StyledPostText = styled.span<{ inverted?: boolean }>`
  cursor: default;
  font-size: 12px !important;
  ${({ inverted }) => (inverted ? 'color: white !important;' : '')}
`;

export const StyledEmail = styled.a`
  display:block;
  font-size:12px;
  margin-top:0px;
`;

const StyledJob = styled.p<{ inverted?: boolean }>`
  font-size: 12px !important;
  ${({ inverted }) => (inverted ? 'color: white !important;' : '')}
  margin: 0px !important;
`;

type IUserChipBase = {
  user: Partial<OpenAPI.Schemas['User']>,
  style?: CSSProperties,
  containerStyle?: { [selector: string]: string },
  height?: number,
  inverted?: string,
  precedingText?: string,
  postText?: string,
  disableLink?: boolean,
  noEmail?: boolean,
  noAvatar?: boolean,
  withShadow?: boolean,
  onClick?: () => void,
  hideJob?: boolean,
  compact?: boolean,
  openInTab?: boolean,
  actionIcon?: SemanticICONS,
  actionClick?: (user: Partial<OpenAPI.Schemas['User']>) => void,
  numberOfLines?: number,
  hideGhost?: boolean,
}

type IUserChip = IUserChipBase

function UserChip({
  user,
  style,
  containerStyle,
  height,
  inverted,
  precedingText,
  postText,
  disableLink,
  noEmail,
  noAvatar,
  withShadow,
  onClick,
  hideJob,
  compact,
  openInTab,
  actionIcon,
  actionClick,
  numberOfLines,
  hideGhost,
}: IUserChip) {
  const history = useHistory();
  const currentUser = useAppSelector<Other.IUserMe>((state) => state.user);

  const userId = user?._id;
  const clicked = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const ghost = useCallback(() => {
    util.confirm('Really ghost as this user?', 'Ghosting allows you to view the platform and carry out actions as though you are the user. You can un-ghost at any time by tapping the avatar at the top-right of the screen.').then(() => api.users.ghost(userId, () => {
      history.push('/');
    }, (err) => toast.error(err.message))).catch(() => { });
  }, [history, userId]);

  if (!user || !user.profile) {
    disableLink = true;
    hideGhost = true;
    actionClick = null;
    user = {
      _id: 'unknown',
      profile: {
        fullName: 'Unknown User',
      }
    }
  }
  const { jobTitle = '', department = '' } = user.profile;
  return (
    <div style={{
      display: 'inline-block', verticalAlign: 'middle', maxWidth: '100%', cursor: !disableLink && 'pointer', ...(containerStyle || {}),
    }}>
      <StyledChip style={style} onClick={clicked} inverted={!!inverted}>
        {/* @ts-ignore */}
        {noAvatar ? null : <StyledAvatar withShadow={!!withShadow} style={disableLink && { pointerEvents: 'none' }} user={user} height={height} target={openInTab ? '_blank' : '_self'} to={!disableLink ? `/users/${user._id}` : '#'} compact={compact} />}
        <StyledText numberOfLines={numberOfLines}>
          {precedingText && <StyledPostText inverted={!!inverted}> {precedingText} </StyledPostText>}
          <StyledName
            target={openInTab ? '_blank' : '_self'}
            /* @ts-ignore */
            inverted={inverted}
            style={disableLink && { pointerEvents: 'none' }}
            to={!disableLink ? `/users/${user._id}` : '#'}
            disableLink={disableLink}
            compact={compact}>
              {user.profile.fullName}
          </StyledName>
          {postText && <StyledPostText inverted={!!inverted}> {postText}</StyledPostText>}
          {((jobTitle || department) && !hideJob)
            && (
              /* @ts-ignore */
              <StyledJob inverted={inverted}>
                {jobTitle}{(jobTitle && department) && ' - '}{department}
              </StyledJob>
            )
          }
          {!noEmail && user.emails?.length > 0
            && <StyledEmail href={`mailto:${user.emails[0].address}`} target='_blank' rel="noopener noreferrer">{user.emails[0].address}</StyledEmail>
          }
        </StyledText>
        {actionIcon && actionClick
          ? (
            <Icon
              name={actionIcon}
              colour={inverted ? 'white' : 'gray'}
              onClick={() => actionClick(user)}
            />
          ) : (
            <>
              {(!(user?._id === currentUser?._id) && util.isInRole(currentUser, 'super.ghost', user.organisation)) && !hideGhost
                ? (
                  <Icon
                    name='snapchat ghost'
                    inverted
                    onClick={ghost}
                  />
                ) : null
              }
            </>
          )
        }

      </StyledChip>
    </div>
  );
}

export default UserChip;
